import React from 'react';
import { Box, Typography, Chip, Button, useTheme, Paper } from '@mui/material';
import { ConfirmationNumber, AssignmentInd, Schedule, Launch } from '@mui/icons-material';
import { getConfig } from '../config/environment';

export interface TicketData {
  ticket_id: string;
  ticket_number?: string;
  summary: string;
  description: string;
  status: string;
  priority: string;
  assigned_team?: string;
  estimated_resolution?: string;
}

interface TicketPreviewProps {
  ticket: TicketData;
}

const TicketPreview: React.FC<TicketPreviewProps> = ({ ticket }) => {
  const theme = useTheme();

  const getPriorityColor = (priority: string): "default" | "error" | "warning" | "success" | "primary" | "secondary" | "info" => {
    switch (priority.toLowerCase()) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        p: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        bgcolor: theme.palette.background.paper,
        maxWidth: '100%',
        width: '100%'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <ConfirmationNumber sx={{ fontSize: 16, color: theme.palette.primary.main }} />
            <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
              {ticket.ticket_number || 'New Ticket'}
            </Typography>
            <Chip 
              label={(ticket.status || 'OPEN').toUpperCase()} 
              size="small" 
              color="primary" 
              variant="outlined"
              sx={{ height: 20, fontSize: '0.65rem' }} 
            />
          </Box>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {ticket.summary}
          </Typography>
        </Box>
        <Chip 
          label={(ticket.priority || 'MEDIUM').toUpperCase()} 
          size="small" 
          color={getPriorityColor(ticket.priority || 'medium')}
          sx={{ height: 24, fontWeight: 600 }}
        />
      </Box>

      {ticket.assigned_team && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: theme.palette.text.secondary }}>
          <AssignmentInd sx={{ fontSize: 16 }} />
          <Typography variant="body2">
            Assigned to: <strong>{ticket.assigned_team}</strong>
          </Typography>
        </Box>
      )}

      {ticket.estimated_resolution && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: theme.palette.text.secondary }}>
          <Schedule sx={{ fontSize: 16 }} />
          <Typography variant="body2">
            ETA: {ticket.estimated_resolution}
          </Typography>
        </Box>
      )}

      <Button
        variant="outlined"
        size="small"
        endIcon={<Launch sx={{ fontSize: 16 }} />}
        href={`${getConfig().ticketsUrl}/tickets/${ticket.ticket_id}`}
        target="_blank"
        fullWidth
        sx={{ 
          mt: 1,
          borderColor: theme.palette.divider,
          color: theme.palette.text.primary,
          '&:hover': {
            borderColor: theme.palette.primary.main,
            bgcolor: theme.palette.action.hover
          }
        }}
      >
        View Ticket Details
      </Button>
    </Paper>
  );
};

export default TicketPreview;
